/**
 * Projects list hooks (React Query)
 *
 * `useProjects` backs the homeowner list; `useProjectsDashboard` backs the
 * enriched designer/contractor dashboard view. Detail-level project data
 * (single project, tasks, milestones, etc.) is migrated separately.
 */

import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import {
	getProjectDocuments,
	type ProjectDocumentListItem,
} from "../services/project-documents-api.service";
import {
	type ActivityLog,
	fetchProjectProposals,
	getProjectActivity,
	getProjectById,
	getProjectContractors,
	getProjectMilestones,
	getProjectPayments,
	getProjectRequests,
	getProjectShortlist,
	getProjects,
	getProjectsDashboard,
	getProjectTasks,
	type HomeownerProfile,
	type Milestone,
	type ProjectContractorItem,
	type ProjectProposalItem,
	type ProjectRequest,
	type ShortlistEntry,
	type Task,
} from "../services/projects-api.service";

/**
 * The signed-in homeowner's projects. `enabled` lets callers skip the fetch
 * for user types that render a different view (designers/contractors).
 *
 * Refetches on every mount, ignoring the global staleTime: each row carries a
 * proposal count that changes when a designer submits from their own session,
 * and nothing in the homeowner's session invalidates this query when that
 * happens.
 */
export function useProjects(enabled = true) {
	return useQuery({
		queryKey: queryKeys.projects.list({ scope: "mine" }),
		queryFn: () => getProjects(),
		enabled,
		refetchOnMount: "always",
	});
}

/** Single project by ID. Returns null if not found. */
export function useProject(projectId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.projects.detail(projectId),
		queryFn: () => getProjectById(projectId),
		enabled: enabled && !!projectId,
	});
}

/**
 * Enriched projects dashboard (designers/contractors). Throws on failure.
 *
 * Refetches on every mount, ignoring the global staleTime: a designer is
 * assigned to a project from the homeowner's session, and nothing in the
 * designer's session invalidates this query when that happens.
 */
export function useProjectsDashboard() {
	return useQuery({
		queryKey: queryKeys.projects.list({ view: "dashboard" }),
		queryFn: () => getProjectsDashboard(),
		refetchOnMount: "always",
	});
}

/** Project activity logs. */
export function useProjectActivity(projectId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.projects.activity(projectId),
		queryFn: () => getProjectActivity(projectId),
		enabled: enabled && !!projectId,
	});
}

/** Project milestones. */
export function useProjectMilestones(projectId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.projects.milestones(projectId),
		queryFn: () => getProjectMilestones(projectId),
		enabled: enabled && !!projectId,
	});
}

/** Project payments. */
export function useProjectPayments(projectId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.projects.payments(projectId),
		queryFn: () => getProjectPayments(projectId),
		enabled: enabled && !!projectId,
	});
}

/** Project contractors. */
export function useProjectContractors(projectId: string, enabled = true) {
	return useQuery<{
		contractors: ProjectContractorItem[];
		disputeWindowHours: number;
	}>({
		queryKey: queryKeys.projects.contractors(projectId),
		queryFn: () => getProjectContractors(projectId),
		enabled: enabled && !!projectId,
	});
}

/**
 * Project designer requests. Throws on failure.
 *
 * Refetches on every mount, ignoring the global staleTime: a designer answers
 * a request from their own session, and nothing in the homeowner's session
 * invalidates this query when that happens.
 */
export function useProjectRequests(projectId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.projects.requests(projectId),
		queryFn: () => getProjectRequests(projectId),
		enabled: enabled && !!projectId,
		refetchOnMount: "always",
	});
}

/**
 * Project designer requests with their proposals. Throws on failure.
 *
 * Refetches on every mount, for the reason `useProjectRequests` does.
 */
export function useProjectProposals(projectId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.projects.proposals(projectId),
		queryFn: () => fetchProjectProposals(projectId),
		enabled: enabled && !!projectId,
		refetchOnMount: "always",
	});
}

/** Project documents. */
export function useProjectDocuments(projectId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.projects.documents(projectId),
		queryFn: () => getProjectDocuments(projectId),
		enabled: enabled && !!projectId,
	});
}

/** Project tasks. */
export function useProjectTasks(projectId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.projects.tasks(projectId),
		queryFn: () => getProjectTasks(projectId),
		enabled: enabled && !!projectId,
	});
}

/** Project shortlist/selections. */
export function useProjectShortlist(projectId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.projects.shortlist(projectId),
		queryFn: () => getProjectShortlist(projectId),
		enabled: enabled && !!projectId,
	});
}

export type {
	ActivityLog,
	HomeownerProfile,
	Milestone,
	ProjectContractorItem,
	ProjectDocumentListItem,
	ProjectProposalItem,
	ProjectRequest,
	ShortlistEntry,
	Task,
};
